import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import {
  listPlacementSessions,
  createPlacementSession,
} from "../api/client";

// Batches = placement sessions. A batch groups students by graduating year or
// drive and (from the credits phase on) holds the interview credit pool. A
// batch must exist before any student can be added.

function formatDate(d) {
  if (!d) return null;
  // API returns YYYY-MM-DD; render as a friendly, locale-independent label.
  const [y, m, day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mi = parseInt(m, 10) - 1;
  if (Number.isNaN(mi) || mi < 0 || mi > 11) return d;
  return `${months[mi]} ${parseInt(day, 10)}, ${y}`;
}

function StatusPill({ status }) {
  const map = {
    active: "bg-success/15 text-success",
    archived: "bg-muted text-muted-foreground",
    completed: "bg-primary/10 text-primary",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize " +
        (map[status] || "bg-muted text-muted-foreground")
      }
    >
      {status || "active"}
    </span>
  );
}

function BatchCard({ batch, onViewStudents, onManageCredits }) {
  const dateRange =
    batch.startDate || batch.endDate
      ? `${formatDate(batch.startDate) || "—"} → ${formatDate(batch.endDate) || "—"}`
      : null;

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-foreground">{batch.name}</h3>
          {batch.academicYear && (
            <p className="mt-0.5 text-sm text-muted-foreground">{batch.academicYear}</p>
          )}
        </div>
        <StatusPill status={batch.status} />
      </div>

      {dateRange && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Calendar" size={15} />
          <span>{dateRange}</span>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Users" size={13} /> Students
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {batch.studentCount ?? 0}
            {batch.studentLimit != null && (
              <span className="text-sm font-normal text-muted-foreground"> / {batch.studentLimit}</span>
            )}
          </div>
        </div>
        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Wallet" size={13} /> Credits
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {batch.creditsRemaining ?? 0}
            <span className="text-sm font-normal text-muted-foreground">
              {" "}/ {batch.includedInterviewCredits ?? 0}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          iconName="Users"
          onClick={() => onViewStudents(batch)}
        >
          View students
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="Wallet"
          onClick={() => onManageCredits(batch)}
        >
          {batch.includedInterviewCredits === 0 ? "Add credits" : "Manage credits"}
        </Button>
      </div>
    </div>
  );
}

const EMPTY_FORM = {
  name: "",
  academicYear: "",
  startDate: "",
  endDate: "",
  studentLimit: "",
};

export default function Batches() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await listPlacementSessions();
      setBatches(rows);
    } catch (err) {
      setLoadError(err?.error || "Could not load batches.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name.trim()) {
      setFormError("Batch name is required.");
      return;
    }
    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      setFormError("Start date must be on or before the end date.");
      return;
    }
    const payload = { name: form.name.trim() };
    if (form.academicYear.trim()) payload.academicYear = form.academicYear.trim();
    if (form.startDate) payload.startDate = form.startDate;
    if (form.endDate) payload.endDate = form.endDate;
    if (form.studentLimit !== "") {
      const n = parseInt(form.studentLimit, 10);
      if (Number.isNaN(n) || n < 0) {
        setFormError("Student limit must be a positive number.");
        return;
      }
      payload.studentLimit = n;
    }

    setSaving(true);
    try {
      await createPlacementSession(payload);
      setModalOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.error || "Could not create the batch.");
    } finally {
      setSaving(false);
    }
  };

  const goToStudents = (batch) =>
    navigate(`/institution/students?batch=${encodeURIComponent(batch.id)}`);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Batches</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Group students by graduating year or placement drive.
          </p>
        </div>
        <Button iconName="Plus" onClick={openCreate}>
          New batch
        </Button>
      </div>

      {/* Load error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl border border-border bg-muted/40" />
          ))}
        </div>
      ) : batches.length === 0 && !loadError ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="FolderPlus" size={24} />
          </span>
          <h3 className="mt-4 text-base font-semibold text-foreground">No batches yet</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create your first batch to start adding students and running interview
            campaigns for your placement cell.
          </p>
          <Button className="mt-5" iconName="Plus" onClick={openCreate}>
            Create your first batch
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {batches.map((b) => (
            <BatchCard
              key={b.id}
              batch={b}
              onViewStudents={goToStudents}
              onManageCredits={() => navigate("/institution/credits")}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={modalOpen}
        onClose={() => (saving ? null : setModalOpen(false))}
        title="New batch"
        description="A batch groups students for a placement season."
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} loading={saving} iconName="Check">
              Create batch
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-4">
          <Input
            label="Batch name"
            required
            placeholder="e.g. 2026 Placement Drive"
            value={form.name}
            onChange={setField("name")}
            autoFocus
          />
          <Input
            label="Academic year"
            placeholder="e.g. 2025–26"
            value={form.academicYear}
            onChange={setField("academicYear")}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={setField("startDate")}
            />
            <Input
              label="End date"
              type="date"
              value={form.endDate}
              onChange={setField("endDate")}
            />
          </div>
          <Input
            label="Student limit"
            type="number"
            min="0"
            placeholder="Optional — leave blank for no cap"
            description="Caps how many students this batch can hold."
            value={form.studentLimit}
            onChange={setField("studentLimit")}
          />
          {formError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {formError}
            </p>
          )}
          {/* Allow Enter-to-submit without a duplicate visible button. */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </Modal>
    </div>
  );
}
